import {
  Table as TableComponent,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Button,
  getKeyValue,
  Skeleton,
  cn
} from '@heroui/react';
import { ArrowDown, NavArrowLeft, NavArrowRight } from 'iconoir-react';
import React, { useMemo } from 'react';

type SortDescriptor = {
  column: string | number;
  direction: 'ascending' | 'descending';
};

type Key = string | number;

type TableItem = {
  key: string;
  [columnKey: string]: unknown;
};

type Handlers = {
  [key: string]: () => void;
};

type TableColumn = {
  key: string;
  label: string;
  sortable?: boolean;
  testId?: string;
};

export type RenderCellProps<
  T extends TableItem = TableItem,
  THandlers = Handlers
> = {
  item: T;
  columnKey: Key;
  handlers?: THandlers;
  isLoading?: boolean;
};

export type SortConfig = {
  key: string;
  direction: 'asc' | 'desc' | 'default';
};

type InfiniteScrollConfig = {
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  loadMoreText?: string;
};

type TableProps<T extends TableItem = TableItem, THandlers = Handlers> = {
  ariaLabel: string;
  'data-testid'?: string;
  data: T[];
  isLoading: boolean;
  rowsPerPage?: number;
  pages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  columns: TableColumn[];
  hideHeader?: boolean;
  renderCell?: (props: RenderCellProps<T, THandlers>) => React.ReactNode;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => void;
  sortConfig?: SortConfig;
  onSort?: (key: string) => void;
  useFlexLayout?: boolean;
  enableClientSidePagination?: boolean;
  onRowClick?: (item: T) => void;
  isRowSelected?: (item: T) => boolean;
  variant?: 'bordered' | 'borderless';
  cellFontWeight?: 'normal' | 'medium';
  infiniteScroll?: InfiniteScrollConfig;
  paginationPrevTestId?: string;
  paginationNextTestId?: string;
};

export function Table<T extends TableItem = TableItem, THandlers = Handlers>({
  ariaLabel,
  'data-testid': dataTestId,
  data,
  isLoading,
  rowsPerPage,
  pages = 0,
  currentPage = 0,
  onPageChange,
  columns,
  hideHeader = true,
  renderCell,
  sortDescriptor,
  onSortChange,
  useFlexLayout = true,
  enableClientSidePagination = false,
  onRowClick,
  isRowSelected,
  variant = 'bordered',
  cellFontWeight = 'medium',
  infiniteScroll,
  paginationPrevTestId,
  paginationNextTestId
}: TableProps<T, THandlers>) {
  const isBordered = variant === 'bordered';
  const skeletonData = useMemo(() => {
    if (!isLoading) return [];
    return Array.from({ length: rowsPerPage || 10 }, (_, index) => ({
      key: `skeleton-${index}`
    })) as T[];
  }, [isLoading, rowsPerPage]);

  const paginatedData = useMemo(() => {
    if (!enableClientSidePagination) return data;

    const startIndex = (currentPage - 1) * (rowsPerPage || 10);
    const endIndex = startIndex + (rowsPerPage || 10);
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, rowsPerPage, enableClientSidePagination]);

  const totalPages = useMemo(() => {
    if (!enableClientSidePagination) return pages;
    return Math.ceil(data.length / (rowsPerPage || 10));
  }, [data.length, rowsPerPage, pages, enableClientSidePagination]);

  const classNames = useMemo(
    () => ({
      wrapper: [
        'p-0',
        variant === 'bordered'
          ? 'border border-solid border-outline'
          : 'border-b border-solid border-outline',
        'shadow-none'
      ],
      tr: [
        'border-solid border-outline border-b last:border-b-0',
        ...(useFlexLayout ? ['flex'] : [])
      ],
      td: [
        'p-4 pr-3',
        'text-sm',
        cellFontWeight === 'normal' ? 'font-normal' : 'font-medium',
        'tracking-[0.1px]',
        'has-[.deployment-actions]:ml-auto'
      ],
      base: ['gap-6'],
      thead: [
        'bg-transparent',
        '[&>tr:last-child]:!mt-0 [&>tr:last-child]:!h-0'
      ],
      th: [
        'bg-transparent',
        'text-xs',
        'font-semibold',
        'text-on-surface-variant',
        'uppercase'
      ]
    }),
    [useFlexLayout, variant, cellFontWeight]
  );

  const cellRenderer = useMemo(
    () => (item: T, columnKey: Key) => {
      if (renderCell) {
        return renderCell({ item, columnKey, isLoading });
      }

      return isLoading ? (
        <Skeleton className='h-4 w-20 rounded' />
      ) : (
        getKeyValue(item, columnKey)
      );
    },
    [renderCell, isLoading]
  );

  const renderBottomContent = () => {
    if (infiniteScroll) {
      return infiniteScroll.hasMore && data.length > 0 ? (
        <div className='flex justify-center py-4'>
          <Button
            color='primary'
            isLoading={infiniteScroll.isLoadingMore}
            onPress={infiniteScroll.onLoadMore}
            data-testid={
              dataTestId
                ? `${dataTestId}-button-load-more`
                : 'common-table-button-load-more'
            }
          >
            {infiniteScroll.loadMoreText || 'Load More'}
          </Button>
        </div>
      ) : null;
    }

    if (totalPages > 0 && onPageChange) {
      const useCustomButtons =
        paginationPrevTestId != null || paginationNextTestId != null;
      return (
        <div className={cn('ml-auto flex', useCustomButtons && 'gap-1.5')}>
          {useCustomButtons && (
            <button
              type='button'
              data-testid={paginationPrevTestId}
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 2)}
              aria-label='Previous page'
              className='inline-flex min-w-8 w-8 h-8 items-center justify-center rounded-lg bg-transparent text-on-surface transition-colors hover:bg-default-100 disabled:opacity-50 disabled:pointer-events-none'
            >
              <NavArrowLeft className='text-lg' />
            </button>
          )}
          <Pagination
            size='sm'
            data-testid={
              dataTestId
                ? `${dataTestId}-pagination`
                : 'common-table-pagination'
            }
            variant='light'
            color='default'
            classNames={{
              wrapper: 'gap-1.5',
              item: 'text-sm font-semibold text-on-surface tracking-[0.1px]',
              cursor: 'text-sm font-semibold text-on-surface tracking-[0.1px]',
              prev: 'text-md text-on-surface',
              next: 'text-md text-on-surface'
            }}
            showControls={!useCustomButtons}
            page={currentPage}
            total={totalPages}
            onChange={page => onPageChange(page - 1)}
          />
          {useCustomButtons && (
            <button
              type='button'
              data-testid={paginationNextTestId}
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage)}
              aria-label='Next page'
              className='inline-flex min-w-8 w-8 h-8 items-center justify-center rounded-lg bg-transparent text-on-surface transition-colors hover:bg-default-100 disabled:opacity-50 disabled:pointer-events-none'
            >
              <NavArrowRight className='text-lg' />
            </button>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <TableComponent
      aria-label={ariaLabel}
      data-testid={dataTestId}
      hideHeader={hideHeader}
      classNames={classNames}
      sortDescriptor={sortDescriptor}
      onSortChange={onSortChange}
      bottomContentPlacement='outside'
      bottomContent={renderBottomContent()}
      sortIcon={<ArrowDown className='icon-smallest text-gray-400' />}
    >
      <TableHeader columns={columns}>
        {column => (
          <TableColumn
            key={column.key}
            className={cn('h-12 px-4', isBordered && 'pl-4 pr-1')}
            allowsSorting={column.sortable}
            data-testid={column.testId}
          >
            <span className='text-xs font-semibold uppercase leading-5 tracking-[1px] text-on-surface-variant'>
              {column.label}
            </span>
          </TableColumn>
        )}
      </TableHeader>

      <TableBody
        items={
          isLoading && !infiniteScroll?.isLoadingMore
            ? skeletonData
            : (paginatedData ?? [])
        }
      >
        {item => {
          const isSelected = isRowSelected?.(item);
          const hasRowClick = !!onRowClick;
          const isSkeletonRow = item.key.toString().startsWith('skeleton');
          return (
            <TableRow
              key={item.key}
              className={cn(
                'h-[45px]',
                'hover:bg-default-100',
                hasRowClick && !isSkeletonRow && 'cursor-pointer',
                isSelected && 'bg-primary-50',
                isBordered && 'h-[77px]'
              )}
              data-testid={
                dataTestId && !isSkeletonRow
                  ? `${dataTestId}-row-${item.key}`
                  : undefined
              }
              onClick={
                hasRowClick && !isSkeletonRow
                  ? () => onRowClick?.(item)
                  : undefined
              }
            >
              {columnKey => (
                <TableCell className={cn(!isBordered && 'py-[9px] px-4')}>
                  {cellRenderer(item, columnKey)}
                </TableCell>
              )}
            </TableRow>
          );
        }}
      </TableBody>
    </TableComponent>
  );
}
